import dayjs from 'dayjs';
import { AppModalForm } from 'components/organisms';
import {
    AppModal,
    DatePickerElement,
    DescriptionInputElement,
    FormHeader,
    FormNavButtons,
} from 'components/molecules';
import { HeaderContainer } from 'components/organisms/AppForm/AppForm.styled';
import { commonTexts, settlementTexts } from 'consts/text';
import { AppFormItem, AppRadioButton, AppRadioGroup } from 'components/atoms';
import { formFields } from 'consts/form/formFields';
import { CurrencyInputElement } from 'components/molecules/FormElements/CurrencyInputElement/CurrencyInputElement';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { AddBillFormFields } from 'types/forms/addBill';
import {
    BalanceTargetType,
    ChildId,
    ChildPaymentMethod,
    ChildPaymentType,
    CommonFormProps,
    FormPageModeDependedTexts,
    PaymentId,
} from 'types';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useEffect } from 'react';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { useWatch } from 'antd/es/form/Form';
import { useTransactionSubmission } from 'hooks/useTransactionSubmission/useTransactionSubmission';
import { If } from '../../../atoms/If/If';

type ChildAddBillModalType = {
    childId?: ChildId | null;
    transactionId?: PaymentId | null;
    onCancel: () => void;
} & Pick<CommonFormProps, 'mode' | 'initialValues'>;

const ADD_MIN_VALUE = 0;
const EDIT_MIN_VALUE = -999999999;

const addModeTexts: FormPageModeDependedTexts = {
    title: settlementTexts.children.addBill.title,
    buttonText: commonTexts.actionLabels.add,
};

const editModeTexts: FormPageModeDependedTexts = {
    title: settlementTexts.children.editBill.title,
    buttonText: commonTexts.actionLabels.save,
    buttonIcon: <IconDeviceFloppy size={20} />,
};

export const ChildAddEditBillModal = ({
    childId,
    transactionId,
    onCancel,
    mode = 'add',
}: ChildAddBillModalType) => {
    const { id: institutionId } = useCurrentInstitution();

    const isAddMode = mode === 'add';
    const { isSubmitting, submit } = useTransactionSubmission(
        JSON.stringify([institutionId, childId, mode, transactionId]),
    );
    const { title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;

    const { form, formInitialData, setFormData } = useEditForm<AddBillFormFields>();

    const { displaySuccess } = useSimpleNotification();

    const { data: transactionData, isFetching: isTransactionDataFetching } = useAppQuery(
        'CHILD_TRANSACTION',
        [childId, transactionId],
        () =>
            StaffHTTPService.settlements.getChildTransaction({
                childId: childId as ChildId,
                transactionId: transactionId as PaymentId,
            }),
        {
            refetchOnWindowFocus: false,

            enabled: !isAddMode,
        },
    );

    useEffect(() => {
        if (mode === 'add') {
            form.resetFields();
            return;
        }
        if (transactionData) {
            setFormData({
                amount: transactionData.amount,
                childTransactionType: transactionData.type,
                childPaymentFormType: transactionData.paymentMethod,
                balanceTargetType: transactionData.category,
                date: parseStringIntoDayjsDate(transactionData.dateTime),
                description: transactionData.description || '',
            });
        }
    }, [transactionData, form, setFormData, mode]);

    const {
        mutateAsync: addTransactionCost,
        error: addTransactionCostError,
        isLoading: isAddTransactionCostLoading,
    } = useAppMutation(StaffHTTPService.settlements.addChildTransactionCost, {
        key: ['ADD_BILL'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.successfullyAddTransactionMessage);
            onCancel();
        },
        invalidateQueryKeys: [
            ['CHILD_SETTLEMENT_BALANCE'],
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['MONTHLY_SETTLEMENTS_V2'],
            ['CHILD_SETTLEMENT_COST_SPLIT'],
        ],
    });

    const {
        mutateAsync: addTransactionPayment,
        error: addTransactionPaymentError,
        isLoading: isAddTransactionPaymentLoading,
    } = useAppMutation(StaffHTTPService.settlements.addChildTransactionPayment, {
        key: ['ADD_BILL'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.successfullyAddTransactionMessage);
            onCancel();
        },
        invalidateQueryKeys: [
            ['CHILD_SETTLEMENT_BALANCE'],
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['MONTHLY_SETTLEMENTS_V2'],
            ['CHILD_SETTLEMENT_COST_SPLIT'],
        ],
    });

    const {
        mutateAsync: editBill,
        error: editBillError,
        isLoading: isEditBillLoading,
    } = useAppMutation(StaffHTTPService.settlements.editChildTransaction, {
        key: ['EDIT_BILL'],
        onSuccess: () => {
            displaySuccess(settlementTexts.children.successfullyEditTransactionMessage);
            onCancel();
        },
        invalidateQueryKeys: [
            ['CHILD_SETTLEMENT_BALANCE'],
            ['SETTLEMENTS'],
            ['CHILD_SETTLEMENT'],
            ['CHILD_SETTLEMENT_PAYMENT'],
            ['CHILD_TRANSACTION', childId, transactionId],
        ],
    });

    const childTransactionTypeWatch = useWatch(formFields.childTransactionType, form);

    const onAddBillFinish = async (billData: AddBillFormFields) => {
        const parsedTransactionAddEditData = {
            amount: billData.amount,
            paymentMethod: billData.childPaymentFormType,
            category: billData.balanceTargetType,
            date: parseDayjsDateIntoString(billData.date, 'YYYY-MM-DD'),
            description: billData.description || '',
            institutionId,
        };

        try {
            await submit(async (paymentRequestId) => {
                if (isAddMode) {
                    const transactionAddDataPayload = {
                        childId: childId as ChildId,
                        institutionId,
                        amount: billData.amount,
                        category: billData.balanceTargetType,
                        date: parseDayjsDateIntoString(billData.date, 'YYYY-MM-DD'),
                        description: billData.description || '',
                    };
                    if (billData.childTransactionType === ChildPaymentType.COST) {
                        await addTransactionCost({ transactionAddData: transactionAddDataPayload });
                    } else {
                        await addTransactionPayment({
                            transactionAddData: {
                                ...transactionAddDataPayload,
                                paymentRequestId,
                                paymentMethod: billData.childPaymentFormType,
                            },
                        });
                    }
                } else {
                    await editBill({
                        transactionId: transactionId as PaymentId,
                        transactionUpdateData: {
                            date: parseDayjsDateIntoString(billData.date, 'YYYY-MM-DD'),
                            amount: parsedTransactionAddEditData.amount,
                            description: billData.description || '',
                            category: billData.balanceTargetType,
                            paymentMethod: billData.childPaymentFormType,
                            type: billData.childTransactionType,
                        },
                    });
                }
            });
        } catch {
            // useAppMutation displays the error; keep the ID so a retry cannot add a duplicate.
        }
    };

    const billAddError =
        addTransactionCostError || addTransactionPaymentError
            ? commonTexts.errorMessages.unknown
            : '';
    const paymentAddError =
        addTransactionPaymentError?.response?.status === 409
            ? 'Ta wpłata została już zapisana z innymi danymi. Sprawdź listę wpłat przed dodaniem kolejnej.'
            : billAddError;
    const billEditError = editBillError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppModal
            width={700}
            open={!!childId}
            onCancel={onCancel}
            closable={!isSubmitting}
            maskClosable={!isSubmitting}
            keyboard={!isSubmitting}
            isLoading={
                isSubmitting ||
                isAddTransactionCostLoading ||
                isAddTransactionPaymentLoading ||
                isEditBillLoading ||
                isTransactionDataFetching
            }
        >
            <AppModalForm<AddBillFormFields>
                error={isAddMode ? paymentAddError : billEditError}
                name="addBill"
                form={form}
                onFinish={onAddBillFinish}
            >
                <HeaderContainer isFormHeaderAtBottom={false}>
                    <FormHeader title={title} desktopTitleLevel={4} />
                </HeaderContainer>
                <DatePickerElement initialValue={dayjs()} allowClear={false} />
                <AppFormItem
                    disabled={!isAddMode}
                    label={settlementTexts.children.addBill.settlementType}
                    name={formFields.childTransactionType}
                >
                    <AppRadioGroup disabled={!isAddMode}>
                        <AppRadioButton value={ChildPaymentType.PAYMENT}>
                            {settlementTexts.children.addBill.charge}
                        </AppRadioButton>
                        <AppRadioButton value={ChildPaymentType.COST}>
                            {settlementTexts.children.addBill.cost}
                        </AppRadioButton>
                    </AppRadioGroup>
                </AppFormItem>
                <If condition={childTransactionTypeWatch === ChildPaymentType.PAYMENT}>
                    <AppFormItem
                        disabled={!isAddMode}
                        label={settlementTexts.children.addBill.refundMethod}
                        name={formFields.childPaymentFormType}
                    >
                        <AppRadioGroup disabled={!isAddMode}>
                            <AppRadioButton value={ChildPaymentMethod.BANK_TRANSFER}>
                                {settlementTexts.children.paymentTypes.transfer}
                            </AppRadioButton>
                            <AppRadioButton value={ChildPaymentMethod.CASH}>
                                {settlementTexts.children.paymentTypes.cash}
                            </AppRadioButton>
                        </AppRadioGroup>
                    </AppFormItem>
                </If>
                <AppFormItem
                    disabled={!isAddMode}
                    label={settlementTexts.children.addBill.balanceTargetType}
                    name={formFields.balanceTargetType}
                >
                    <AppRadioGroup disabled={!isAddMode}>
                        <AppRadioButton value={BalanceTargetType.PARENT}>
                            {settlementTexts.children.balanceTargetTypes.parentOrLegalGuardian}
                        </AppRadioButton>
                        <AppRadioButton value={BalanceTargetType.FOUNDATION}>
                            {settlementTexts.children.balanceTargetTypes.anotherEntity}
                        </AppRadioButton>
                        <AppRadioButton value={BalanceTargetType.CATERING}>
                            {settlementTexts.children.balanceTargetTypes.catering}
                        </AppRadioButton>
                    </AppRadioGroup>
                </AppFormItem>
                <CurrencyInputElement
                    name={formFields.amount}
                    label={commonTexts.dataLabels.amount}
                    min={isAddMode ? ADD_MIN_VALUE : EDIT_MIN_VALUE}
                />
                <DescriptionInputElement optional hasOptionalText />
                <FormNavButtons
                    isSubmitting={
                        isSubmitting ||
                        isAddTransactionCostLoading ||
                        isAddTransactionPaymentLoading ||
                        isEditBillLoading
                    }
                    formValuesBeforeChanges={formInitialData}
                    buttonText={buttonText}
                    withPlus={isAddMode}
                    leftIcon={buttonIcon}
                />
            </AppModalForm>
        </AppModal>
    );
};
